import {
  convertNutrientAmount,
  type NutrientUnit,
} from "./nutrients";

export type NutrientId =
  | "vitamin-a"
  | "vitamin-b1"
  | "vitamin-b2"
  | "vitamin-b3"
  | "vitamin-b5"
  | "vitamin-b6"
  | "vitamin-b7"
  | "vitamin-b9"
  | "vitamin-b12"
  | "vitamin-c"
  | "vitamin-d"
  | "vitamin-e"
  | "vitamin-k"
  | "choline"
  | "calcium"
  | "chromium"
  | "copper"
  | "fluoride"
  | "iodine"
  | "iron"
  | "magnesium"
  | "manganese"
  | "molybdenum"
  | "phosphorus"
  | "potassium"
  | "selenium"
  | "sodium"
  | "zinc";

export type NutrientCategory = "vitamin" | "mineral";
export type PreferredNutrientUnit = "mg" | "mcg";

export interface NutrientDefinition {
  readonly id: NutrientId;
  /** User-facing NRV name. */
  readonly name: string;
  readonly displayName: string;
  readonly category: NutrientCategory;
  /** Preferred NRV mass unit. */
  readonly unit: PreferredNutrientUnit;
  readonly preferredUnit: PreferredNutrientUnit;
  /** Established labels and keys accepted at data-import and migration boundaries. */
  readonly aliases: readonly string[];
  /** Exact keys exposed in Open Food Facts' `nutriments` object. */
  readonly openFoodFactsKeys: readonly string[];
}

export interface PreferredNutrientAmount {
  readonly amount: number;
  readonly unit: PreferredNutrientUnit;
}

type NutrientDefinitionInput = Omit<NutrientDefinition, "name" | "unit">;

function defineNutrient(definition: NutrientDefinitionInput): NutrientDefinition {
  return Object.freeze({
    ...definition,
    name: definition.displayName,
    unit: definition.preferredUnit,
    aliases: Object.freeze([...definition.aliases]),
    openFoodFactsKeys: Object.freeze([...definition.openFoodFactsKeys]),
  });
}

/**
 * Vitamins, vitamin-like nutrients, minerals and trace elements for which the
 * Australian/New Zealand Nutrient Reference Values define reference intakes.
 * Preferred units follow the units used by the NRV summary tables.
 */
export const NUTRIENT_CATALOG: readonly NutrientDefinition[] = Object.freeze([
  defineNutrient({
    id: "vitamin-a",
    displayName: "Vitamin A",
    category: "vitamin",
    preferredUnit: "mcg",
    aliases: [
      "vitamin_a",
      "retinol",
      "retinol equivalent",
      "retinol equivalents",
      "vitamin a retinol equivalents",
    ],
    openFoodFactsKeys: ["vitamin-a_100g"],
  }),
  defineNutrient({
    id: "vitamin-b1",
    displayName: "Thiamin (Vitamin B1)",
    category: "vitamin",
    preferredUnit: "mg",
    aliases: [
      "thiamin",
      "thiamine",
      "vitamin b1",
      "vitamin_b1",
      "vitamin b-1",
      "b1",
      "b-1",
      "thiamin b1",
      "thiamin_b1",
      "vitamin-b1-thiamin",
      "vitamin_b1_thiamin",
    ],
    openFoodFactsKeys: ["vitamin-b1_100g"],
  }),
  defineNutrient({
    id: "vitamin-b2",
    displayName: "Riboflavin (Vitamin B2)",
    category: "vitamin",
    preferredUnit: "mg",
    aliases: [
      "riboflavin",
      "vitamin b2",
      "vitamin_b2",
      "vitamin b-2",
      "b2",
      "b-2",
      "riboflavin b2",
      "riboflavin_b2",
      "vitamin-b2-riboflavin",
      "vitamin_b2_riboflavin",
    ],
    openFoodFactsKeys: ["vitamin-b2_100g"],
  }),
  defineNutrient({
    id: "vitamin-b3",
    displayName: "Niacin (Vitamin B3)",
    category: "vitamin",
    preferredUnit: "mg",
    aliases: [
      "niacin",
      "niacin equivalents",
      "vitamin b3",
      "vitamin_b3",
      "vitamin b-3",
      "b3",
      "b-3",
      "vitamin pp",
      "vitamin_pp",
      "nicotinic acid",
      "nicotinamide",
      "niacinamide",
      "vitamin-b3-niacin",
      "vitamin_b3_niacin",
    ],
    openFoodFactsKeys: ["vitamin-pp_100g"],
  }),
  defineNutrient({
    id: "vitamin-b6",
    displayName: "Vitamin B6",
    category: "vitamin",
    preferredUnit: "mg",
    aliases: [
      "vitamin_b6",
      "vitamin b-6",
      "b6",
      "b-6",
      "pyridoxine",
      "pyridoxal",
      "pyridoxamine",
    ],
    openFoodFactsKeys: ["vitamin-b6_100g"],
  }),
  defineNutrient({
    id: "vitamin-b12",
    displayName: "Vitamin B12",
    category: "vitamin",
    preferredUnit: "mcg",
    aliases: [
      "vitamin_b12",
      "vitamin b-12",
      "b12",
      "b-12",
      "cobalamin",
      "cyanocobalamin",
      "methylcobalamin",
    ],
    openFoodFactsKeys: ["vitamin-b12_100g"],
  }),
  defineNutrient({
    id: "vitamin-b9",
    displayName: "Folate (Vitamin B9)",
    category: "vitamin",
    preferredUnit: "mcg",
    aliases: [
      "folate",
      "folic acid",
      "folacin",
      "dietary folate equivalents",
      "vitamin b9",
      "vitamin_b9",
      "vitamin b-9",
      "b9",
      "b-9",
      "vitamin-b9-folate",
      "vitamin_b9_folate",
    ],
    openFoodFactsKeys: ["vitamin-b9_100g"],
  }),
  defineNutrient({
    id: "vitamin-b5",
    displayName: "Pantothenic acid (Vitamin B5)",
    category: "vitamin",
    preferredUnit: "mg",
    aliases: [
      "pantothenic acid",
      "pantothenate",
      "pantothenic_acid",
      "vitamin b5",
      "vitamin_b5",
      "vitamin b-5",
      "b5",
      "b-5",
    ],
    openFoodFactsKeys: ["pantothenic-acid_100g"],
  }),
  defineNutrient({
    id: "vitamin-b7",
    displayName: "Biotin (Vitamin B7)",
    category: "vitamin",
    preferredUnit: "mcg",
    aliases: [
      "biotin",
      "vitamin b7",
      "vitamin_b7",
      "vitamin b-7",
      "b7",
      "b-7",
      "vitamin h",
    ],
    openFoodFactsKeys: ["biotin_100g"],
  }),
  defineNutrient({
    id: "vitamin-c",
    displayName: "Vitamin C",
    category: "vitamin",
    preferredUnit: "mg",
    aliases: ["vitamin_c", "ascorbic acid", "ascorbate"],
    openFoodFactsKeys: ["vitamin-c_100g"],
  }),
  defineNutrient({
    id: "vitamin-d",
    displayName: "Vitamin D",
    category: "vitamin",
    preferredUnit: "mcg",
    aliases: [
      "vitamin_d",
      "calciferol",
      "vitamin d2",
      "vitamin_d2",
      "ergocalciferol",
      "vitamin d3",
      "vitamin_d3",
      "cholecalciferol",
    ],
    openFoodFactsKeys: ["vitamin-d_100g"],
  }),
  defineNutrient({
    id: "vitamin-e",
    displayName: "Vitamin E",
    category: "vitamin",
    preferredUnit: "mg",
    aliases: [
      "vitamin_e",
      "tocopherol",
      "alpha tocopherol",
      "alpha-tocopherol",
      "alpha tocopherol equivalents",
      "alpha-te",
      "a-te",
    ],
    openFoodFactsKeys: ["vitamin-e_100g"],
  }),
  defineNutrient({
    id: "vitamin-k",
    displayName: "Vitamin K",
    category: "vitamin",
    preferredUnit: "mcg",
    aliases: [
      "vitamin_k",
      "vitamin k1",
      "vitamin_k1",
      "phylloquinone",
      "vitamin k2",
      "vitamin_k2",
      "menaquinone",
      "menaquinone 7",
      "mk-7",
      "vitamin k2 (mk-7)",
      "vitamin-k2-mk-7",
    ],
    openFoodFactsKeys: ["vitamin-k_100g"],
  }),
  defineNutrient({
    id: "choline",
    displayName: "Choline",
    category: "vitamin",
    preferredUnit: "mg",
    aliases: ["total choline"],
    openFoodFactsKeys: [],
  }),
  defineNutrient({
    id: "calcium",
    displayName: "Calcium",
    category: "mineral",
    preferredUnit: "mg",
    aliases: [],
    openFoodFactsKeys: ["calcium_100g"],
  }),
  defineNutrient({
    id: "chromium",
    displayName: "Chromium",
    category: "mineral",
    preferredUnit: "mcg",
    aliases: [],
    openFoodFactsKeys: ["chromium_100g"],
  }),
  defineNutrient({
    id: "copper",
    displayName: "Copper",
    category: "mineral",
    preferredUnit: "mg",
    aliases: [],
    openFoodFactsKeys: ["copper_100g"],
  }),
  defineNutrient({
    id: "fluoride",
    displayName: "Fluoride",
    category: "mineral",
    preferredUnit: "mg",
    aliases: [],
    openFoodFactsKeys: ["fluoride_100g"],
  }),
  defineNutrient({
    id: "iodine",
    displayName: "Iodine",
    category: "mineral",
    preferredUnit: "mcg",
    aliases: [],
    openFoodFactsKeys: ["iodine_100g"],
  }),
  defineNutrient({
    id: "iron",
    displayName: "Iron",
    category: "mineral",
    preferredUnit: "mg",
    aliases: [],
    openFoodFactsKeys: ["iron_100g"],
  }),
  defineNutrient({
    id: "magnesium",
    displayName: "Magnesium",
    category: "mineral",
    preferredUnit: "mg",
    aliases: [],
    openFoodFactsKeys: ["magnesium_100g"],
  }),
  defineNutrient({
    id: "manganese",
    displayName: "Manganese",
    category: "mineral",
    preferredUnit: "mg",
    aliases: [],
    openFoodFactsKeys: ["manganese_100g"],
  }),
  defineNutrient({
    id: "molybdenum",
    displayName: "Molybdenum",
    category: "mineral",
    preferredUnit: "mcg",
    aliases: [],
    openFoodFactsKeys: ["molybdenum_100g"],
  }),
  defineNutrient({
    id: "phosphorus",
    displayName: "Phosphorus",
    category: "mineral",
    preferredUnit: "mg",
    aliases: ["phosphorous"],
    openFoodFactsKeys: ["phosphorus_100g"],
  }),
  defineNutrient({
    id: "potassium",
    displayName: "Potassium",
    category: "mineral",
    preferredUnit: "mg",
    aliases: [],
    openFoodFactsKeys: ["potassium_100g"],
  }),
  defineNutrient({
    id: "selenium",
    displayName: "Selenium",
    category: "mineral",
    preferredUnit: "mcg",
    aliases: [],
    openFoodFactsKeys: ["selenium_100g"],
  }),
  defineNutrient({
    id: "sodium",
    displayName: "Sodium",
    category: "mineral",
    preferredUnit: "mg",
    aliases: [],
    openFoodFactsKeys: ["sodium_100g"],
  }),
  defineNutrient({
    id: "zinc",
    displayName: "Zinc",
    category: "mineral",
    preferredUnit: "mg",
    aliases: [],
    openFoodFactsKeys: ["zinc_100g"],
  }),
]);

const DEFINITIONS_BY_ID = new Map<NutrientId, NutrientDefinition>(
  NUTRIENT_CATALOG.map((definition) => [definition.id, definition]),
);

const DEFINITIONS_BY_ALIAS = buildAliasIndex();

export function getNutrientDefinition(id: string): NutrientDefinition | undefined {
  return DEFINITIONS_BY_ID.get(id as NutrientId);
}

/** Resolves only a normalized exact ID, display name, alias or provider key. */
export function resolveNutrient(input: string): NutrientDefinition | undefined {
  return DEFINITIONS_BY_ALIAS.get(normalizeAlias(input));
}

export function convertToPreferredNutrientUnit(
  definition: NutrientDefinition,
  amount: number,
  fromUnit: NutrientUnit | string,
): PreferredNutrientAmount {
  return {
    amount: convertNutrientAmount(amount, fromUnit, definition.preferredUnit),
    unit: definition.preferredUnit,
  };
}

function buildAliasIndex(): ReadonlyMap<string, NutrientDefinition> {
  const index = new Map<string, NutrientDefinition>();
  for (const definition of NUTRIENT_CATALOG) {
    const values = [
      definition.id,
      definition.displayName,
      ...definition.aliases,
      ...definition.openFoodFactsKeys,
    ];
    for (const value of values) {
      const alias = normalizeAlias(value);
      const existing = index.get(alias);
      if (existing && existing.id !== definition.id) {
        throw new Error(`Nutrient alias ${value} is shared by ${existing.id} and ${definition.id}`);
      }
      index.set(alias, definition);
    }
  }
  return index;
}

function normalizeAlias(input: string): string {
  return input
    .normalize("NFKC")
    .trim()
    .toLocaleLowerCase("en-AU")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ");
}
